import { useStore } from "lib/store/store";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PREMIUM_HUB_DASHBOARD_URL } from "lib/hubs";
import { persistPage } from "lib/routes";
import { cn } from "lib/utils";

function Sidebar() {
  const { sidebar, isMobile, toggleSidebar, setIsMobile, page, setPage, themeDirection } =
    useStore();
  const navigate = useNavigate();

  const go = (path: string, nextPage: string) => {
    setPage(nextPage);
    persistPage(nextPage);
    navigate(path);
  };

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        document.body.style.overflow = "";
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [setIsMobile]);

  useEffect(() => {
    if (isMobile) {
      document.body.style.overflow = sidebar ? "hidden" : "";
    }
  }, [sidebar, isMobile]);

  const navActive =
    "flex items-center gap-[10px] p-3 mb-[5px] rounded-[8px] text-sm text-[var(--dash-text-strong)] bg-[var(--dash-sidebar-item)] border-s-[3px] border-s-[var(--theme-accent)] no-underline";
  const navIdle =
    "flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all text-sm text-[var(--dash-text)] hover:bg-[var(--dash-sidebar-item)] hover:text-[var(--dash-text-strong)]";

  const closedOffscreen =
    themeDirection === "rtl" ? "translate-x-full" : "-translate-x-full";

  return (
    <>
      <div
        id="overlay"
        onClick={toggleSidebar}
        className={`fixed inset-0 bg-[var(--dash-overlay)] z-[998] transition-opacity duration-300 md:hidden ${
          sidebar && isMobile ? "opacity-100" : "opacity-0 hidden"
        }`}
      />

      <aside
        id="sidebar"
        className={cn(
          "dashboard-scroll fixed inset-y-0 start-0 z-[999] flex w-64 flex-col overflow-y-auto border-e border-[var(--dash-border)] bg-[var(--dash-sidebar)] p-5 transition-transform duration-300",
          sidebar || !isMobile ? "translate-x-0" : closedOffscreen,
          "md:translate-x-0",
        )}
      >
        <div className="flex items-center justify-between mb-8">
          <div className="text-[var(--dash-text-strong)] text-xl font-bold flex items-center gap-2">
            <i className="fab fa-telegram text-[#229ED9]"></i> Telegram Hub
          </div>
          <button
            id="sidebarCloseBtn"
            onClick={toggleSidebar}
            className="md:hidden text-[var(--dash-text-strong)] p-2 hover:bg-black/5 dark:hover:bg-white/10 rounded-full"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <nav className="space-y-1">
          <div
            className={
              page === "front" || page === "tgb" || page === "tgv" || page === "tgt"
                ? navActive
                : navIdle
            }
            onClick={() => go("/", "front")}
          >
            <i className="fas fa-th-large w-5"></i> Dashboard
          </div>
          <div
            className={page === "manage-tools" ? navActive : navIdle}
            onClick={() => go("/manage-tools", "manage-tools")}
          >
            <i className="fas fa-tools w-5"></i> Login & Manage Tools
          </div>
          <div
            className={page === "more-tools" ? navActive : navIdle}
            onClick={() => go("/more-tools", "more-tools")}
          >
            <i className="fas fa-toolbox w-5"></i> Explore the Toolkit
          </div>
          <div
            className={page === "demos" ? navActive : navIdle}
            onClick={() => go("/demos", "demos")}
          >
            <i className="fas fa-laptop-code w-5"></i> Demos
          </div>

          <div
            className={
              page === "trending"
                ? "flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all text-sm text-[#FFD700] font-bold bg-yellow-400/5 border border-yellow-400/20 bg-yellow-400/15"
                : "flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all text-sm text-[#FFD700] font-bold bg-yellow-400/5 border border-yellow-400/20 hover:bg-yellow-400/15"
            }
            onClick={() => go("/offers", "trending")}
          >
            <i className="fas fa-fire w-5"></i> Diwali Offers
            <span className="ms-auto bg-[#ff4757] text-white text-[10px] px-1.5 py-0.5 rounded font-bold animate-pulse-custom">
              HOT
            </span>
          </div>

          <div
            className={page === "support" ? navActive : navIdle}
            onClick={() => go("/support", "support")}
          >
            <i className="fas fa-headset w-5"></i> Support
          </div>
          <a
            href={PREMIUM_HUB_DASHBOARD_URL}
            className={navIdle}
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="fas fa-cubes w-5"></i> Explore Premium Hub
          </a>
        </nav>
      </aside>
    </>
  );
}

export default Sidebar;
